/**
 * What the wall shows for a scope and search (Part 04 decision 3; Part 07
 * decision 3): one flat list of ids, whether anything is still on its way,
 * and the next page.
 *
 * - The project: this window's store-driven feed (`stores/wall-feed-store.ts`),
 *   in its order.
 * - Everything: the scope feed's ids (`use-wall-scope-feed.ts`), in the
 *   server's order, newest first, under the current project's cards that
 *   arrived since that page loaded (a quick note, an agent's new frame),
 *   so they show at the top at once instead of on the next refresh.
 * - Archived: the scope feed's ids, newest first.
 * - A search: the scope feed's ids, in rank order.
 *
 * Each window loads its own first page and Pinned row
 * when its feed is not built for the current project and frames-store
 * generation — a mirror window, or after the owner's frames were replaced.
 * The owner window first waits until its own project load has landed
 * (`framesLoadedSpaceId`, `isLoadingCanvas`), so it never doubles that load.
 */
import { useCallback, useEffect, useMemo } from "react";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import { loadNextWallPage, loadWallTools } from "@/lib/wall-feed-loader";
import { useWorkspaceLoadStore } from "@/stores/workspace-load-store";
import { useFramesStore } from "@/stores/frames-store";
import { useWallFeedStore, useWallFrameIds } from "@/stores/wall-feed-store";
import type { WallScope } from "@/stores/wall-scope-store";
import { useWallScopeFeed } from "./use-wall-scope-feed";
import { wallFrame } from "./wall-frame-source";

export interface WallFeedView {
  /** The cards, in the order they show. */
  ids: readonly string[];
  /** Nothing to show yet because the first page is on its way. */
  loading: boolean;
  /** The last page did not load; `retry` asks for it again. */
  failed: boolean;
  /** The Pinned row's page has landed for this project. */
  toolsLoaded: boolean;
  /** The Pinned row's page has landed or failed to: the row is final. */
  toolsSettled: boolean;
  /** More pages may follow the loaded ones. */
  hasMore: boolean;
  loadMore: () => void;
  retry: () => void;
}

/** A card's creation time in ms, or NaN when it has none to compare. */
function createdMs(createdAt: string | undefined): number {
  return createdAt ? Date.parse(createdAt) : Number.NaN;
}

/** A loaded card of the current project. */
const projectCreatedMs = (id: string) =>
  createdMs(useFramesStore.getState().framesById[id]?.createdAt);

/** The loaded ones of scoped ids (which may arrive in another order), newest first. */
function newestFirst(ids: readonly string[]): string[] {
  const items = ids.flatMap((id) => {
    const at = projectCreatedMs(id);
    return Number.isNaN(at) ? [] : [{ id, at }];
  });
  items.sort((left, right) =>
    left.at === right.at ? right.id.localeCompare(left.id) : right.at - left.at,
  );
  return items.map((item) => item.id);
}

/**
 * The current project's cards newer than Everything's newest loaded card and
 * not on its page yet: they arrived since it loaded. Everything's page is
 * the server's, so these would otherwise wait for its next refresh. While
 * none of the page's cards can be dated, nothing counts as an arrival.
 */
function projectArrivals(
  projectIds: readonly string[],
  scopedIds: readonly string[],
): string[] {
  const loaded = new Set(scopedIds);
  let newest = Number.NEGATIVE_INFINITY;
  // Everything's page holds other projects' cards too.
  for (const id of scopedIds) {
    const at = createdMs(wallFrame(id)?.createdAt);
    if (at > newest) newest = at;
  }
  if (scopedIds.length > 0 && newest === Number.NEGATIVE_INFINITY) return [];
  return projectIds.filter(
    (id) => !loaded.has(id) && projectCreatedMs(id) > newest,
  );
}

/**
 * Load this window's first page and Pinned row when they are missing. The
 * owner first lets its own project load (`loadWorkspaceFrames`) finish.
 */
function useWallFeedLoad(projectId: string): {
  feedBuilt: boolean;
  toolsLoaded: boolean;
  toolsFailed: boolean;
} {
  const generation = useFramesStore((s) => s.generation);
  const ownerSettled = useWorkspaceLoadStore(
    (s) =>
      getWindowRoleSync() === "mirror" ||
      (!s.isLoadingCanvas && s.framesLoadedSpaceId === projectId),
  );
  const feedBuilt = useWallFeedStore(
    (s) => s.projectId === projectId && s.builtForGeneration === generation,
  );
  const toolsLoaded = useWallFeedStore(
    (s) =>
      s.toolsLoadedFor?.projectId === projectId &&
      s.toolsLoadedFor.generation === generation,
  );
  const toolsFailed = useWallFeedStore(
    (s) =>
      s.toolsFailedFor?.projectId === projectId &&
      s.toolsFailedFor.generation === generation,
  );

  useEffect(() => {
    if (!ownerSettled) return;
    if (!feedBuilt) void loadNextWallPage(projectId).catch(() => {});
    if (!toolsLoaded) void loadWallTools(projectId).catch(() => {});
  }, [feedBuilt, ownerSettled, projectId, toolsLoaded]);

  return { feedBuilt, toolsLoaded, toolsFailed };
}

export function useWallFeedView(
  projectId: string,
  scope: WallScope,
  query: string,
  /** The wall shows in this window. */
  visible: boolean,
): WallFeedView {
  const { feedBuilt, toolsLoaded, toolsFailed } = useWallFeedLoad(projectId);
  const scoped = useWallScopeFeed(projectId, scope, query, visible);
  const projectIds = useWallFrameIds();
  const firstPageLoading = useWallFeedStore(
    (s) => s.loading && s.pagesLoaded === 0,
  );
  const feedFailed = useWallFeedStore((s) => s.failed);
  const feedHasMore = useWallFeedStore((s) => s.hasMore);
  const ownerLoading = useWorkspaceLoadStore((s) => s.isLoadingCanvas);
  const searching = query.trim() !== "";

  const kind = scope.kind;
  const scopedLoading = scoped.loading;
  const ids = useMemo(() => {
    if (!scoped.ids) return projectIds;
    if (searching) return scoped.ids;
    if (kind === "archived") return newestFirst(scoped.ids);
    // Before Everything's first page lands there is nothing to be newer than.
    if (scopedLoading && scoped.ids.length === 0) return scoped.ids;
    const arrivals = projectArrivals(projectIds, scoped.ids);
    return arrivals.length > 0 ? [...arrivals, ...scoped.ids] : scoped.ids;
  }, [kind, projectIds, scoped.ids, scopedLoading, searching]);

  const { loadMore: scopedLoadMore, retry: scopedRetry } = scoped;
  const scopedActive = scoped.ids !== null;
  const loadMore = useCallback(() => {
    if (scopedActive) scopedLoadMore();
    else void loadNextWallPage(projectId).catch(() => {});
  }, [projectId, scopedActive, scopedLoadMore]);
  // The project feed's next page is the one that failed.
  const retry = scopedActive ? scopedRetry : loadMore;

  return {
    ids,
    loading: scopedActive
      ? scoped.loading
      : ownerLoading || !feedBuilt || firstPageLoading,
    failed: scopedActive ? scoped.failed : feedBuilt && feedFailed,
    toolsLoaded,
    toolsSettled: toolsLoaded || toolsFailed,
    hasMore: scopedActive ? scoped.hasMore : feedHasMore,
    loadMore,
    retry,
  };
}
